// Your routes — and the navigation binding's, which is the part worth knowing.
//
// THIS FILE IS REPLACED when the navigation shell is re-applied. `bottom-tabs`,
// `stack` and `sidebar` produce different routers, so the binding owns it and
// `appfactory spec apply` rewrites it with `overwrite: true`.
//
// It used to claim the opposite — "the factory never replaces it" — which was
// wrong and expensive: an apply silently took a TABS array full of hand-picked
// labels and icons with it, under a message saying four files were written.
// The apply now says loudly when it REPLACES rather than creates.
//
// So: edit it freely, and commit before running `spec apply`. Everything below
// is yours until the shell itself changes.

import { createRouter, createMemoryHistory } from '@ionic/vue-router';

import TabsShell from './pages/TabsShell.vue';

// The screens themselves come from ./product-routes.js, which is generated from
// .appfactory/product-plan.json by `appfactory product apply`.
//
// Two owners, one file each. THIS file is the shell — bottom-tabs, stack and
// sidebar produce different routers, and the binding rewrites it on every
// `spec apply`. The PRODUCT owns which screens exist. Before this split the
// shell hardcoded the worked example's three pages, so re-applying a shell
// silently deleted every screen the app had grown.
import { PRODUCT_ROUTES, DESTINATION_ROUTES, HOME_ROUTE } from './product-routes.js';

/** Where the app opens. main.js pushes this. */
export const HOME = HOME_ROUTE;

/** Icons a generated tab falls back to, in order. Reorder or replace freely. */
const TAB_ICONS = [homeOutline, listOutline];

// The tabs, in bar order, with the icon each one wears.
//
// `icon` is an IMPORTED ionicon, not a name. `<ion-icon name="home">` fetches
// its SVG from a CDN at runtime, which the app's content-security-policy
// blocks and an offline app could not reach anyway — you get an invisible tab
// bar that works fine on your machine and is blank on a plane.
//
// Every tab is a route. A tab bar is for DESTINATIONS — if your app's main verb
// needs a permanent home, give it a real screen; if it is an action, it belongs
// on the screen it acts on, the way "Add an item" does on Home.
import { homeOutline, listOutline } from 'ionicons/icons';

// DESTINATION_ROUTES, not PRIMARY_ROUTES, and the difference is a tab that
// could never work.
//
// `primary` in the plan grades IMPORTANCE — set_plan says so explicitly, and
// this app marks both `shelf` and `capture` primary because both are
// load-bearing. But `capture` lives at `/recipe/:recipeId/capture`, so
// building a tab out of it produced a permanent bar button whose href
// contained a literal `:recipeId`. It matched no route and could not: there is
// no recipe in a tab bar, only on the screen you came from.
//
// A tab bar is for DESTINATIONS — top-level screens you navigate TO — which is
// exactly what `destination` marks and what DESTINATION_ROUTES already
// computed. It was sitting unused two lines below the array that needed it.
export const TABS = [
  ...DESTINATION_ROUTES.map((r, i) => ({
    name: r.id,
    label: r.label,
    path: r.path,
    // A tab without an icon renders as a label floating in empty space, so
    // there is always one. Change them freely — this array is yours.
    icon: TAB_ICONS[i % TAB_ICONS.length],
  })),
];

// NO `{ path: '/', redirect: HOME }` RECORD HERE. It looks like the obvious way
// to send the app to its home screen and it is a trap whenever the plan's
// primary screen sits at '/', which is the common case: HOME_ROUTE is then '/'
// too, so the record redirects '/' to '/', vue-router follows it forever, and
// `router.push(HOME)` in main.js throws
// `RangeError: Maximum call stack size exceeded` SYNCHRONOUSLY.
//
// That throw aborts the rest of main.js — before `router.isReady()`, before
// `mount('#app')`, before the QA harness and `data-ready` are installed. The
// result is not a broken screen, it is NO screen: #app stays empty on every
// route, the build passes, every structural check passes, and all eight store
// screenshots come back blank with "the app never rendered".
//
// Reproduced in headless Chrome, and fixed by deleting the record: '/' then
// matches the shelf child below directly. A child path beginning with '/' is
// absolute in vue-router, so the product routes keep their planned paths
// ('/keeping', '/recipe/:recipeId') rather than living under '/tabs/', and
// deep links to them still resolve.
export default createRouter({
  history: createMemoryHistory(),
  routes: [
    {
      // The tab shell owns its own outlet, so each tab keeps its own
      // navigation stack. Pushing /tabs/items/3 leaves the tab bar in place
      // and the back button working, and switching away and back returns you
      // to the detail screen rather than the top of the list.
      path: '/tabs/',
      component: TabsShell,
      children: [
        { path: '', redirect: HOME },
        // `props: true` on every route, so a screen with `:id` in its path gets
        // it as a prop as well as through useRoute(). A detail screen that has
        // to reach for the router to learn which record it is showing is a
        // screen that cannot be tested without one.
        ...PRODUCT_ROUTES.map((r) => ({
          path: r.path.replace(/^\/tabs\//, ''),
          name: r.id,
          component: r.component,
          props: true,
        })),
      ],
    },
  ],
});
